<template>
	<template v-if="reporterName">
		<dt>Created by</dt>
		<dd class="flex flex-gap-8 items-center">
			<button
				:class="{'bordered-button': showBorderAroundReporterButton, 'flex-wrap': isSDK}"
				:disabled="isButtonDisabled"
				class="flex"
				@click="onReporterClick"
			>
				<BaseIcon v-if="isDemo" :name="$store.productIcon" class="w-16 h-16 mt-2 mr-8" />
				<BaseUserAvatar
					v-else-if="!isSDK"
					:url="reporterUser ? reporterUser.avatar : ''"
					:name="reporterName"
					:size="16"
					class="mt-2 mr-8"
					data-test="reporter-avatar"
				/>
				<template v-if="isSDK && session.uploaderEmail">
					<span class="select-all">{{ reporterName }}</span>
					<span class="select-none">&nbsp;(via SDK)</span>
					<BaseIcon v-if="!isCopyButtonClicked" class="copy-email-icon" name="mono/clipboard" />
					<BaseIcon v-else class="copy-email-icon text-green" name="mono/check-circle" />
				</template>
				<template v-else-if="isSDK">
					<span>{{ reporterName }}</span>
					<span>(via SDK)</span>
				</template>
				<span v-else :class="{'text-gray-400': isReporterNameFaded}">
					{{ reporterName }}
				</span>
			</button>
			<BaseTooltip v-if="isReporterNameFaded" text="No longer a workspace member" class="ml-4">
				<BaseIcon
					name="mono/info"
					class="ml-8 mb-2 w-14 h-14 text-gray-400 hover:text-gray-100 cursor-pointer"
				/>
			</BaseTooltip>
		</dd>
	</template>
</template>

<script>
import {storeToRefs} from 'pinia'
import copyToClipboard from 'shared/utils/copyToClipboard'
import {useSessionStore} from 'shared/stores/session'

export default {
	name: 'SessionDetailsReporter',
	setup() {
		const sessionStore = useSessionStore()
		const {session, canUserEditSession} = storeToRefs(sessionStore)
		return {session, canUserEditSession}
	},
	data() {
		return {
			isCopyButtonClicked: false,
			copyClickTimer: null,
		}
	},
	computed: {
		nameType() {
			if (this.isSDK && this.session.uploaderEmail) {
				return 'email'
			}
			return 'name'
		},
		isDemo() {
			return this.session.uploadMethod === 'demo'
		},
		isSDK() {
			return this.session.uploadMethod === 'sdk'
		},
		reporterUser() {
			if (this.$store.isExtension) return
			if (this.isSDK) return
			return this.$store.users.find(user => user.id === this.session.ownerId)
		},
		reporterName() {
			if (this.$store.isExtension) {
				return this.$store.user?.name
			}
			if (this.isSDK) {
				return this.session.uploaderEmail || (this.$store.isUserWorkspaceMember ? 'Anonymous' : '')
			}
			if (this.isDemo) {
				return `Team ${this.$store.productName}`
			}
			if (!this.session.ownerName) return 'Deleted user'
			return this.session.ownerName
		},
		isButtonDisabled() {
			// SDK reporter email can always be copied
			if (this.isSDK) return false
			// Only session editors can use the filter button
			if (!this.canUserEditSession) return true
			// The filter button is disabled when showing removed users, which are filtered by ID
			if (!this.reporterUser) return true
			return false
		},
		isReporterNameFaded() {
			if (this.isSDK) return
			if (!this.$store.isUserWorkspaceMember) return
			if (!this.session.ownerName) return
			// Faded style for browser extension reported sessions,
			// when reporter isn't a workspace member anymore.
			return !this.$store.users.find(user => user.id === this.session.ownerId)
		},
		showBorderAroundReporterButton() {
			if (this.isDemo) return false
			if (this.isSDK) return false
			if (!this.canUserEditSession) return false
			if (!this.reporterUser) return false
			// Only show filter chip for browser extension reported sessions,
			// when reporter user is still workspace member.
			return true
		},
	},
	methods: {
		onReporterClick: function () {
			if (this.isDemo) return
			if (this.isSDK && this.session.uploaderEmail) return this.copyUploaderEmail()
			if (!this.canUserEditSession) return
			return this.goToFilteredSessionsList()
		},
		copyUploaderEmail: function () {
			this.isCopyButtonClicked = true
			this.copyClickTimer = setTimeout(() => {
				this.isCopyButtonClicked = false
				this.copyClickTimer = null
			}, 2000)
			copyToClipboard(this.reporterName)
		},
		goToFilteredSessionsList() {
			if (!this.reporterUser) return
			this.$routerHelpers.push(
				`/workspaces/${this.session.workspaceId}/collections/${this.session.collectionId}?filters=reporter:${this.reporterUser.email}`
			)
		},
	},
}
</script>

<style scoped>
.bordered-button {
	border: 1px solid var(--gray-550);
	border-radius: 8px;
	padding: 3px 8px;
}

.bordered-button:hover {
	background-color: var(--gray-600);
}

.copy-email-icon {
	margin-top: 4px;
	margin-left: 4px;
	color: var(--gray-400);
}

button:hover:has(.copy-email-icon) {
	color: var(--gray-100);
}

button:hover .copy-email-icon {
	color: var(--gray-100);
}
</style>
